import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { ArrowLeft, User, MessageSquare, DollarSign, Bell, CreditCard, Phone, Calendar, TrendingUp, Activity, Bot, CheckCircle, AlertCircle, Clock } from 'lucide-react';

const UserInfo = ({ userId, onBack, interactions, responses, expenses, reminders, subscriptions, users }) => {

    // Encontrar el usuario específico
    const user = users.find(u => u.id === userId);

    // Filtrar datos específicos del usuario
    const userData = useMemo(() => {
        const userInteractions = interactions.filter(i => i.userId === userId);
        const userResponses = responses.filter(r => r.userId === userId);
        const userExpenses = expenses.filter(e => e.userId === userId);
        const userReminders = reminders.filter(r => r.userId === userId);
        const userSubscriptions = subscriptions.filter(s => s.userId === userId);

        return {
            interactions: userInteractions,
            responses: userResponses,
            expenses: userExpenses,
            reminders: userReminders,
            subscriptions: userSubscriptions
        };
    }, [userId, interactions, responses, expenses, reminders, subscriptions]);

    // Función para formatear teléfono
    const formatPhone = (phone) => {
        if (!phone) return 'N/A';
        if (phone.includes(' ') || phone.includes('-')) return phone;
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length >= 10) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '+$1 $2 $3 $4');
        }
        return phone;
    };

    // Función para formatear tiempo
    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleString('es-MX', {
            timeZone: 'America/Phoenix',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calcular estadísticas del usuario
    const getUserStats = () => {
        const totalExpenseAmount = userData.expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
        const pendingReminders = userData.reminders.filter(r => r.status === 'pending').length;
        const activeSubscriptions = userData.subscriptions.filter(s => s.isActive !== false).length;
        const audioMessages = userData.interactions.filter(i => i.messageType === 'audio').length;
        const responseRate = userData.interactions.length > 0 ?
            Math.round((userData.responses.length / userData.interactions.length) * 100) : 0;

        return {
            totalMessages: userData.interactions.length,
            totalExpenses: totalExpenseAmount,
            expenseCount: userData.expenses.length,
            pendingReminders,
            activeSubscriptions,
            audioMessages,
            responseRate
        };
    };

    // Datos para gráficos del usuario
    const getUserChartData = () => {
        // Actividad por hora
        const hourlyActivity = Array.from({ length: 24 }, (_, hour) => ({
            hour: `${hour}:00`,
            messages: userData.interactions.filter(i => new Date(i.timestamp).getHours() === hour).length
        }));

        // Gastos por categoría
        const expensesByCategory = {};
        userData.expenses.forEach(expense => {
            const category = expense.categoryName || 'Sin categoría';
            expensesByCategory[category] = (expensesByCategory[category] || 0) + (expense.amount || 0);
        });

        const categoryData = Object.entries(expensesByCategory).map(([name, value], index) => ({
            name,
            value,
            color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16'][index % 7]
        }));

        // Actividad mensual (últimos 6 meses)
        const monthlyActivity = Array.from({ length: 6 }, (_, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            const monthName = date.toLocaleDateString('es-MX', { month: 'short' });

            const monthInteractions = userData.interactions.filter(interaction => {
                const interactionDate = new Date(interaction.timestamp);
                return interactionDate.getMonth() === date.getMonth() &&
                    interactionDate.getFullYear() === date.getFullYear();
            });

            const monthExpenses = userData.expenses.filter(expense => {
                const expenseDate = new Date(expense.date || expense.createdAt);
                return expenseDate.getMonth() === date.getMonth() &&
                    expenseDate.getFullYear() === date.getFullYear();
            });

            return {
                month: monthName,
                messages: monthInteractions.length,
                expenses: monthExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0)
            };
        }).reverse();

        return { hourlyActivity, categoryData, monthlyActivity };
    };

    // Obtener respuesta asociada a un mensaje - CORREGIDO
    const getMessageResponse = (messageId) => {
        // Buscar por messageId, originalMessageId y también por proximidad temporal
        return userData.responses.find(r =>
            r.messageId === messageId ||
            r.originalMessageId === messageId ||
            r.messageId === messageId
        );
    };

    // Crear conversación completa (mensajes + respuestas ordenados) - MEJORADO
    const getFullConversation = () => {
        console.log('🔍 Creando conversación completa para usuario:', userId);
        console.log('📥 Interacciones del usuario:', userData.interactions.length);
        console.log('🤖 Respuestas del usuario:', userData.responses.length);

        const conversation = [];

        // Agregar todas las interacciones del usuario
        userData.interactions.forEach((interaction, index) => {
            console.log(`👤 Mensaje ${index + 1}:`, {
                id: interaction.id,
                message: interaction.message.substring(0, 50) + '...',
                timestamp: interaction.timestamp
            });

            conversation.push({
                type: 'user',
                timestamp: interaction.timestamp,
                content: interaction.message,
                messageType: interaction.messageType,
                id: interaction.id,
                messageId: interaction.messageId || interaction.id
            });

            // Buscar respuesta asociada de múltiples formas
            let response = userData.responses.find(r =>
                r.messageId === interaction.id ||
                r.originalMessageId === interaction.id ||
                r.messageId === interaction.messageId
            );

            // Si no encuentra respuesta exacta, buscar por proximidad temporal (dentro de 5 minutos)
            if (!response) {
                response = userData.responses.find(r => {
                    const timeDiff = Math.abs(new Date(r.timestamp) - new Date(interaction.timestamp));
                    return timeDiff < 300000; // 5 minutos en milisegundos
                });
            }

            if (response) {
                console.log(`🤖 Respuesta encontrada para mensaje ${interaction.id}:`, {
                    responseId: response.id,
                    response: response.response.substring(0, 50) + '...',
                    timestamp: response.timestamp
                });

                conversation.push({
                    type: 'bot',
                    timestamp: response.timestamp,
                    content: response.response,
                    id: response.id,
                    relatedMessageId: interaction.id
                });
            } else {
                console.log(`❌ No se encontró respuesta para mensaje ${interaction.id}`);
            }
        });

        // Agregar respuestas que no estén asociadas a ningún mensaje específico
        userData.responses.forEach(response => {
            const alreadyAdded = conversation.some(item =>
                item.type === 'bot' && item.id === response.id
            );

            if (!alreadyAdded) {
                console.log('🤖 Respuesta sin mensaje asociado:', {
                    id: response.id,
                    response: response.response.substring(0, 50) + '...'
                });

                conversation.push({
                    type: 'bot',
                    timestamp: response.timestamp,
                    content: response.response,
                    id: response.id,
                    orphan: true // Marcar como respuesta huérfana
                });
            }
        });

        const sortedConversation = conversation.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        console.log('✅ Conversación final:', {
            totalMessages: sortedConversation.length,
            userMessages: sortedConversation.filter(m => m.type === 'user').length,
            botMessages: sortedConversation.filter(m => m.type === 'bot').length,
            orphanResponses: sortedConversation.filter(m => m.orphan).length
        });

        return sortedConversation;
    };

    const stats = getUserStats();
    const { hourlyActivity, categoryData, monthlyActivity } = getUserChartData();
    const conversation = getFullConversation();

    if (!user) {
        return (
            <div className="p-6 text-center">
                <div className="text-red-500 mb-4">Usuario no encontrado</div>
                <button
                    onClick={onBack}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Volver
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header con información del usuario */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={onBack}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5 text-gray-600" />
                            </button>
                            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {formatPhone(user.phone)}
                                </h1>
                                <p className="text-sm text-gray-500">ID: {user.id}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-500">
                                Miembro desde {formatTime(user.firstInteraction)}
                            </div>
                            <div className="text-sm text-gray-500">
                                Última actividad: {formatTime(user.lastInteraction)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPIs del usuario */}
                <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{stats.totalMessages}</div>
                            <div className="text-xs text-gray-500">Mensajes</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">${stats.totalExpenses.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">Total Gastos</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{stats.expenseCount}</div>
                            <div className="text-xs text-gray-500">Transacciones</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{stats.pendingReminders}</div>
                            <div className="text-xs text-gray-500">Recordatorios</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-pink-600">{stats.activeSubscriptions}</div>
                            <div className="text-xs text-gray-500">Suscripciones</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">{stats.audioMessages}</div>
                            <div className="text-xs text-gray-500">Audios</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-600">{stats.responseRate}%</div>
                            <div className="text-xs text-gray-500">Respuestas</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gráficos de actividad */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad por Hora</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={hourlyActivity}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="messages" fill="#3B82F6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {categoryData.length > 0 && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gastos por Categoría</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={categoryData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {categoryData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Monto']} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Tendencia mensual */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Mensual (6 meses)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip />
                        <Bar yAxisId="left" dataKey="messages" fill="#3B82F6" name="Mensajes" />
                        <Line yAxisId="right" type="monotone" dataKey="expenses" stroke="#10B981" strokeWidth={3} name="Gastos ($)" />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Conversación completa */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Conversación Completa ({conversation.length} mensajes)
                    </h3>
                </div>

                <div className="p-6">
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {conversation.map((item, index) => (
                            <div key={`${item.type}-${item.id}-${index}`} className={`flex ${item.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${item.type === 'user'
                                        ? 'bg-blue-600 text-white'
                                        : item.orphan
                                            ? 'bg-yellow-100 text-yellow-900 border border-yellow-300'
                                            : 'bg-gray-100 text-gray-900'
                                    }`}>
                                    <div className="flex items-start gap-2">
                                        {item.type === 'bot' && (
                                            <div className="flex items-center gap-1">
                                                <Bot className={`h-4 w-4 mt-1 ${item.orphan ? 'text-yellow-600' : 'text-blue-500'}`} />
                                                {item.orphan && <span className="text-xs text-yellow-600">(Sin contexto)</span>}
                                            </div>
                                        )}
                                        {item.type === 'user' && item.messageType === 'audio' && <Phone className="h-4 w-4 mt-1" />}
                                        <div className="flex-1">
                                            <p className="text-sm">{item.content}</p>
                                            <p className={`text-xs mt-1 ${item.type === 'user' ? 'text-blue-200' :
                                                    item.orphan ? 'text-yellow-600' : 'text-gray-500'
                                                }`}>
                                                {formatTime(item.timestamp)}
                                                {item.type === 'user' && item.messageType === 'audio' && ' (Audio)'}
                                                {item.type === 'bot' && item.relatedMessageId && ' (Respuesta)'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {conversation.length === 0 && (
                            <div className="text-center py-12">
                                <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay conversaciones</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Este usuario aún no ha enviado mensajes.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Debug info - temporal para verificar datos */}
                    {conversation.length > 0 && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm">
                            <h4 className="font-semibold text-gray-700 mb-2">📊 Estadísticas de Conversación:</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                <div>
                                    <span className="font-medium">Total mensajes:</span> {conversation.length}
                                </div>
                                <div>
                                    <span className="font-medium">Del usuario:</span> {conversation.filter(m => m.type === 'user').length}
                                </div>
                                <div>
                                    <span className="font-medium">Del bot:</span> {conversation.filter(m => m.type === 'bot').length}
                                </div>
                                <div>
                                    <span className="font-medium">Sin contexto:</span> {conversation.filter(m => m.orphan).length}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detalles de gastos */}
            {userData.expenses.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Historial de Gastos ({userData.expenses.length})
                        </h3>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {userData.expenses.slice(0, 10).map((expense) => (
                            <div key={expense.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                                        <span className="text-sm">{expense.categoryIcon || '💰'}</span>
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">{expense.description}</div>
                                        <div className="text-sm text-gray-500">
                                            {expense.categoryName || 'Sin categoría'} • {formatTime(expense.createdAt)}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-green-600">
                                        ${expense.amount?.toFixed(2) || '0.00'}
                                    </div>
                                    {expense.location && (
                                        <div className="text-xs text-gray-500">{expense.location}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recordatorios */}
            {userData.reminders.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Recordatorios ({userData.reminders.length})
                        </h3>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {userData.reminders.slice(0, 5).map((reminder) => (
                            <div key={reminder.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className={`h-3 w-3 rounded-full ${reminder.status === 'pending' ? 'bg-yellow-500' :
                                            reminder.status === 'sent' ? 'bg-green-500' : 'bg-gray-500'
                                        }`}></div>
                                    <div>
                                        <div className="font-medium text-gray-900">{reminder.message}</div>
                                        <div className="text-sm text-gray-500">
                                            {formatTime(reminder.date)} • {reminder.status}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    {reminder.status === 'pending' ? (
                                        <Clock className="h-4 w-4 text-yellow-500" />
                                    ) : reminder.status === 'sent' ? (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <AlertCircle className="h-4 w-4 text-gray-500" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Suscripciones */}
            {userData.subscriptions.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Suscripciones ({userData.subscriptions.length})
                        </h3>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {userData.subscriptions.map((subscription) => (
                            <div key={subscription.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                                        <CreditCard className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">{subscription.name}</div>
                                        <div className="text-sm text-gray-500">
                                            {subscription.billingCycle} • {subscription.category || 'Sin categoría'}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-purple-600">
                                        ${subscription.amount?.toFixed(2) || '0.00'}
                                    </div>
                                    <div className={`text-xs ${subscription.isActive === false ? 'text-red-500' : 'text-green-500'}`}>
                                        {subscription.isActive === false ? 'Inactiva' : 'Activa'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserInfo;